import type { Plugin } from 'chart.js';

const FONT = '600 13px Inter, sans-serif';
const HORIZONTAL_LENGTH = 28;
const BEND_OFFSET = 16;
const TEXT_PADDING = 4;
const LABEL_HEIGHT = 16;
const BOX_GAP = 3;
const MIN_SLICE_PERCENT = 0;
const LEFT_CENTER_SLICE_THRESHOLD = 15;

type PieArcPoint = {
  startAngle: number;
  endAngle: number;
  x: number;
  y: number;
  outerRadius: number;
};

type TextBox = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

type LabelLayout = {
  percentage: string;
  slicePercent: number;
  defaultRightSide: boolean;
  startX: number;
  startY: number;
  bendX: number;
  bendY: number;
  endX: number;
  endY: number;
  rightSide: boolean;
  textWidth: number;
  /** When set, vertical placement stays near this Y (left-center labels). */
  anchorY?: number;
};

function endXForSide(bendX: number, rightSide: boolean) {
  return bendX + (rightSide ? HORIZONTAL_LENGTH : -HORIZONTAL_LENGTH);
}

function normalizeAngle(angle: number) {
  const twoPi = Math.PI * 2;
  return ((angle % twoPi) + twoPi) % twoPi;
}

function angleInArc(angle: number, startAngle: number, endAngle: number) {
  const a = normalizeAngle(angle);
  const start = normalizeAngle(startAngle);
  const end = normalizeAngle(endAngle);

  if (start <= end) {
    return a >= start && a <= end;
  }
  return a >= start || a <= end;
}

/** Best attachment point on the left arc (9 o'clock area). */
function leftEdgeAngleOnArc(arc: PieArcPoint) {
  const candidates = [Math.PI, (Math.PI * 5) / 4, (Math.PI * 3) / 4];
  for (const candidate of candidates) {
    if (angleInArc(candidate, arc.startAngle, arc.endAngle)) {
      return candidate;
    }
  }
  const mid = (arc.startAngle + arc.endAngle) / 2;
  return Math.cos(mid) <= 0 ? mid : Math.PI;
}

function shouldUseLeftCenterLayout(angle: number, slicePercent: number, arc: PieArcPoint) {
  if (slicePercent >= LEFT_CENTER_SLICE_THRESHOLD) return true;

  const outwardY = arc.y + Math.sin(angle) * (arc.outerRadius + BEND_OFFSET);
  if (outwardY > arc.y + arc.outerRadius * 0.12) return true;

  return Math.sin(angle) > 0.35;
}

function buildRadialLabel(
  arc: PieArcPoint,
  percentage: string,
  slicePercent: number,
  angle: number,
  ctx: CanvasRenderingContext2D
): LabelLayout {
  const rightSide = Math.cos(angle) >= 0 && Math.sin(angle) <= 0.35;
  const bendX = arc.x + Math.cos(angle) * (arc.outerRadius + BEND_OFFSET);
  const bendY = arc.y + Math.sin(angle) * (arc.outerRadius + BEND_OFFSET);

  return {
    percentage,
    slicePercent,
    defaultRightSide: rightSide,
    startX: arc.x + Math.cos(angle) * arc.outerRadius,
    startY: arc.y + Math.sin(angle) * arc.outerRadius,
    bendX,
    bendY,
    endX: endXForSide(bendX, rightSide),
    endY: bendY,
    rightSide,
    textWidth: ctx.measureText(percentage).width,
  };
}

/** Large / bottom slices: label at left-center (9 o'clock), not below the pie. */
function buildLeftCenterLabel(arc: PieArcPoint, percentage: string, slicePercent: number, ctx: CanvasRenderingContext2D): LabelLayout {
  const attachAngle = leftEdgeAngleOnArc(arc);
  const bendX = arc.x - arc.outerRadius - BEND_OFFSET;
  const bendY = arc.y;

  return {
    percentage,
    slicePercent,
    defaultRightSide: false,
    startX: arc.x + Math.cos(attachAngle) * arc.outerRadius,
    startY: arc.y + Math.sin(attachAngle) * arc.outerRadius,
    bendX,
    bendY,
    endX: bendX - HORIZONTAL_LENGTH,
    endY: bendY,
    rightSide: false,
    textWidth: ctx.measureText(percentage).width,
    anchorY: arc.y,
  };
}

function getTextBox(label: LabelLayout): TextBox {
  const anchorX = label.endX + (label.rightSide ? TEXT_PADDING : -TEXT_PADDING);
  const left = label.rightSide ? anchorX : anchorX - label.textWidth;
  const right = label.rightSide ? anchorX + label.textWidth : anchorX;

  return {
    left,
    right,
    top: label.endY - LABEL_HEIGHT / 2,
    bottom: label.endY + LABEL_HEIGHT / 2,
  };
}

function boxesOverlap(a: TextBox, b: TextBox) {
  return !(a.right + BOX_GAP < b.left || a.left > b.right + BOX_GAP || a.bottom + BOX_GAP < b.top || a.top > b.bottom + BOX_GAP);
}

function hasCollision(label: LabelLayout, placed: LabelLayout[]) {
  const box = getTextBox(label);
  return placed.some((other) => boxesOverlap(box, getTextBox(other)));
}

function applyCandidate(label: LabelLayout, rightSide: boolean, endY: number) {
  label.rightSide = rightSide;
  label.endY = endY;
  label.endX = endXForSide(label.bendX, rightSide);
}

function placeLabels(labels: LabelLayout[]) {
  const placed: LabelLayout[] = [];
  const sorted = [...labels].sort((a, b) => b.slicePercent - a.slicePercent);

  for (const label of sorted) {
    const baseY = label.anchorY ?? label.bendY;
    const yOffsets = label.anchorY !== undefined ? [0, -16, 16, -32, 32] : [0, 18, -18, 36, -36, 54, -54];
    const sides = label.defaultRightSide === false ? [false, true] : [label.defaultRightSide, !label.defaultRightSide];

    let positioned = false;

    for (const rightSide of sides) {
      for (const yOffset of yOffsets) {
        applyCandidate(label, rightSide, baseY + yOffset);

        if (!hasCollision(label, placed)) {
          placed.push(label);
          positioned = true;
          break;
        }
      }
      if (positioned) break;
    }

    if (!positioned) {
      applyCandidate(label, false, baseY);
      placed.push(label);
    }
  }

  return placed;
}

export const PieChartLabelsPlugin: Plugin<'pie'> = {
  id: 'pieLabelPlugin',

  afterDatasetsDraw(chart) {
    const pluginOptions = (
      chart.options.plugins as {
        pieLabelPlugin?: { display?: boolean; preferLeftSide?: boolean; minSlicePercent?: number };
      }
    )?.pieLabelPlugin;

    if (!pluginOptions?.display) return;

    const { ctx } = chart;
    const dataset = chart.data.datasets[0];
    const meta = chart.getDatasetMeta(0);

    if (!dataset || !meta) return;

    const data = dataset.data as number[];
    const total = data.reduce((sum, value) => sum + Number(value), 0);

    if (!total) return;

    const preferLeftSide = pluginOptions.preferLeftSide ?? false;
    const minSlicePercent = pluginOptions.minSlicePercent ?? MIN_SLICE_PERCENT;

    ctx.save();
    ctx.font = FONT;
    ctx.fillStyle = '#444';
    ctx.strokeStyle = '#BDBDBD';
    ctx.lineWidth = 1;
    ctx.textBaseline = 'middle';

    const labels: LabelLayout[] = [];

    for (let index = 0; index < meta.data.length; index++) {
      const arc = meta.data[index] as unknown as PieArcPoint;
      const value = Number(data[index]);
      if (!value) continue;

      const slicePercent = (value / total) * 100;
      if (slicePercent < minSlicePercent) continue;

      const percentage = slicePercent.toFixed(1) + '%';
      const angle = (arc.startAngle + arc.endAngle) / 2;

      if (preferLeftSide && Math.cos(angle) >= 0) continue;

      const label = shouldUseLeftCenterLayout(angle, slicePercent, arc)
        ? buildLeftCenterLabel(arc, percentage, slicePercent, ctx)
        : buildRadialLabel(arc, percentage, slicePercent, angle, ctx);

      labels.push(label);
    }

    const placedLabels = placeLabels(labels);

    placedLabels.forEach((label) => {
      ctx.beginPath();
      ctx.moveTo(label.startX, label.startY);
      ctx.lineTo(label.bendX, label.bendY);
      ctx.lineTo(label.endX, label.endY);
      ctx.stroke();

      ctx.textAlign = label.rightSide ? 'left' : 'right';
      ctx.fillText(label.percentage, label.endX + (label.rightSide ? TEXT_PADDING : -TEXT_PADDING), label.endY);
    });

    ctx.restore();
  },
};

export default PieChartLabelsPlugin;
